import { apiBaseUrl } from '../api/baseUrl';

type McpTextContent = { type: 'text'; text: string };
type McpToolResult = { content: McpTextContent[] };

type ModelContextToolDef = {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations?: { readOnlyHint?: boolean };
  execute: (input: Record<string, unknown>) => Promise<McpToolResult>;
};

type ModelContextProvider = {
  registerTool: (tool: ModelContextToolDef) => void;
};

function getModelContext(): ModelContextProvider | undefined {
  const docMc =
    typeof document !== 'undefined'
      ? (document as Document & { modelContext?: ModelContextProvider }).modelContext
      : undefined;
  const navMc =
    typeof navigator !== 'undefined'
      ? (navigator as Navigator & { modelContext?: ModelContextProvider }).modelContext
      : undefined;
  return docMc ?? navMc;
}

function mcpResult(data: unknown): McpToolResult {
  return {
    content: [{ type: 'text', text: JSON.stringify(data) }],
  };
}

async function apiGet<T>(path: string): Promise<T> {
  const base = apiBaseUrl.replace(/\/$/, '');
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`API ${path} failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export function initWebMcp(): void {
  const mc = getModelContext();
  if (!mc || typeof mc.registerTool !== 'function') return;

  mc.registerTool({
    name: 'getMemorial',
    description: 'Fetch a memorial headstone by grave id from the Pethernity API.',
    inputSchema: {
      type: 'object',
      properties: {
        graveId: { type: 'string', description: 'Headstone / grave identifier' },
      },
      required: ['graveId'],
    },
    annotations: { readOnlyHint: true },
    execute: async (input) => {
      const graveId = String(input.graveId ?? '');
      const headstones = await apiGet<Array<Record<string, unknown>>>('/headstones');
      const match = headstones.find((h) => h.id === graveId);
      if (!match) {
        return mcpResult({ found: false, graveId });
      }
      return mcpResult({ found: true, memorial: match });
    },
  });

  mc.registerTool({
    name: 'listNearbyMemorials',
    description: 'List memorial headstones inside a map viewport bounding box.',
    inputSchema: {
      type: 'object',
      properties: {
        wx1: { type: 'number' },
        wy1: { type: 'number' },
        wx2: { type: 'number' },
        wy2: { type: 'number' },
        limit: { type: 'number', default: 50 },
      },
      required: ['wx1', 'wy1', 'wx2', 'wy2'],
    },
    annotations: { readOnlyHint: true },
    execute: async (input) => {
      const params = new URLSearchParams({
        wx1: String(input.wx1),
        wy1: String(input.wy1),
        wx2: String(input.wx2),
        wy2: String(input.wy2),
        limit: String(input.limit ?? 50),
      });
      return mcpResult(await apiGet(`/headstones/map?${params.toString()}`));
    },
  });

  mc.registerTool({
    name: 'listCommunityPosts',
    description: 'List recent community posts on Pethernity.',
    inputSchema: {
      type: 'object',
      properties: {
        limit: { type: 'number', default: 20 },
      },
    },
    annotations: { readOnlyHint: true },
    execute: async (input) => {
      const limit = Number(input.limit ?? 20);
      return mcpResult(await apiGet(`/posts?limit=${encodeURIComponent(String(limit))}`));
    },
  });
}

initWebMcp();
